import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import type { RefObject } from "react";

interface ComponentSize {
  width: number;
  height: number;
}

const getSize = (el: HTMLElement | null): ComponentSize => {
  if (!el) {
    return {
      width: 0,
      height: 0,
    };
  }

  return {
    width: el.offsetWidth,
    height: el.offsetHeight,
  };
};

const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

const useComponentSize = (ref: RefObject<HTMLElement>): ComponentSize => {
  const [ComponentSize, setComponentSize] = useState<ComponentSize>(
    getSize(ref ? ref.current : null)
  );

  const handleResize = useCallback(() => {
    if (ref.current) {
      setComponentSize(getSize(ref.current));
    }
  }, [ref]);

  useIsomorphicLayoutEffect(() => {
    if (!ref.current) {
      return () => {};
    }

    handleResize();

    if (typeof ResizeObserver === "function") {
      let resizeObserver = new ResizeObserver(() => {
        handleResize();
      });
      resizeObserver.observe(ref.current);

      return () => {
        resizeObserver.disconnect();
        resizeObserver = null;
      };
    }

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleResize, ref]);

  return ComponentSize;
};

export default useComponentSize;
